import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Table, InputGroup } from 'react-bootstrap';
import { FaCopy, FaSync, FaEye, FaEyeSlash } from 'react-icons/fa';

const suggestCodigo = (descripcion = '') => {
  const d = String(descripcion).toUpperCase();
  if (d.includes('DESAY')) return 'DES';
  if (d.includes('ALMU')) return 'ALM';
  if (d.includes('CENA') || d.includes('CENE')) return 'CEN';
  return d.replace(/[^A-Z0-9]/g, '').slice(0, 3) || 'SRV';
};

const ModalComedorLectorOffline = ({
  show,
  onHide,
  onSave,
  estacion,
  comedores = [],
  lectores = [],
  tiposServicio = [],
  onComedorChange,
  onRegenerarToken,
}) => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');

  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [idComedor, setIdComedor] = useState('');
  const [idLector, setIdLector] = useState('');
  const [activo, setActivo] = useState(true);
  const [servicios, setServicios] = useState([]);
  const [error, setError] = useState('');
  const [tokenAcceso, setTokenAcceso] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [regenerando, setRegenerando] = useState(false);

  const lectoresFiltrados = useMemo(() => {
    if (!idComedor) return lectores;
    return lectores.filter((l) => String(l.id_comedor) === String(idComedor));
  }, [lectores, idComedor]);

  useEffect(() => {
    if (!show) return;

    if (estacion) {
      setCodigo(estacion.codigo || '');
      setNombre(estacion.nombre || '');
      setIdComedor(estacion.id_comedor ? String(estacion.id_comedor) : '');
      setIdLector(estacion.id_lector ? String(estacion.id_lector) : '');
      setActivo(estacion.activo !== undefined ? Boolean(estacion.activo) : true);
      setTokenAcceso(estacion.token_acceso || '');

      const existentes = Array.isArray(estacion.servicios) ? estacion.servicios : [];
      setServicios(
        tiposServicio.map((t) => {
          const match = existentes.find(
            (s) => Number(s.id_tipo_servicio_alimentacion) === Number(t.id),
          );
          return {
            id_tipo_servicio_alimentacion: t.id,
            descripcion: t.descripcion || t.nombre || `Servicio ${t.id}`,
            checked: Boolean(match),
            codigo: match?.codigo || suggestCodigo(t.descripcion || t.nombre),
            activo: match?.activo !== undefined ? Boolean(match.activo) : true,
          };
        }),
      );
    } else {
      setCodigo('');
      setNombre('');
      setIdComedor('');
      setIdLector('');
      setActivo(true);
      setTokenAcceso('');
      setServicios(
        tiposServicio.map((t) => ({
          id_tipo_servicio_alimentacion: t.id,
          descripcion: t.descripcion || t.nombre || `Servicio ${t.id}`,
          checked: false,
          codigo: suggestCodigo(t.descripcion || t.nombre),
          activo: true,
        })),
      );
    }
    setError('');
    setShowToken(false);
    setCopyOk(false);
  }, [show, estacion, tiposServicio]);

  const handleCopyToken = async () => {
    if (!tokenAcceso) return;
    try {
      await navigator.clipboard.writeText(tokenAcceso);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch (_err) {
      setError('No se pudo copiar el token al portapapeles');
    }
  };

  const handleRegenerarToken = async () => {
    if (!estacion?.id || !onRegenerarToken) return;
    const ok = window.confirm(
      '¿Regenerar el token?\n\nEl token actual dejará de funcionar en Electron. ' +
        'Si solo cambió el PC de la estación, NO regenere: copie el mismo token en el equipo nuevo.',
    );
    if (!ok) return;
    setRegenerando(true);
    try {
      const updated = await onRegenerarToken(estacion.id);
      if (updated?.token_acceso) {
        setTokenAcceso(updated.token_acceso);
        setShowToken(true);
      }
    } catch (_err) {
      // toast en hook
    } finally {
      setRegenerando(false);
    }
  };
  const handleComedorSelect = (value) => {
    setIdComedor(value);
    setIdLector('');
    if (onComedorChange && value) {
      onComedorChange(Number(value));
    }
  };

  const toggleServicio = (idTipo) => {
    setServicios((prev) =>
      prev.map((s) =>
        Number(s.id_tipo_servicio_alimentacion) === Number(idTipo)
          ? { ...s, checked: !s.checked }
          : s,
      ),
    );
  };

  const updateServicioCodigo = (idTipo, value) => {
    setServicios((prev) =>
      prev.map((s) =>
        Number(s.id_tipo_servicio_alimentacion) === Number(idTipo)
          ? { ...s, codigo: String(value || '').toUpperCase().slice(0, 20) }
          : s,
      ),
    );
  };

  const handleSave = () => {
    if (!codigo.trim() || !nombre.trim() || !idComedor || !idLector) {
      setError('Código, nombre, comedor y lector son obligatorios');
      return;
    }
    if (!selectedProject?.id) {
      setError('No hay proyecto seleccionado en la sesión');
      return;
    }

    const seleccionados = servicios.filter((s) => s.checked);
    if (!seleccionados.length) {
      setError('Seleccione al menos un servicio habilitado (desayuno/almuerzo/cena)');
      return;
    }
    if (seleccionados.some((s) => !String(s.codigo || '').trim())) {
      setError('Cada servicio seleccionado debe tener código (DES, ALM, CEN, ...)');
      return;
    }

    const codigos = seleccionados.map((s) => s.codigo.trim().toUpperCase());
    if (new Set(codigos).size !== codigos.length) {
      setError('Los códigos de servicio no pueden repetirse');
      return;
    }

    onSave({
      codigo: codigo.trim().toUpperCase(),
      nombre: nombre.trim(),
      id_proyecto: Number(selectedProject.id),
      id_comedor: Number(idComedor),
      id_lector: Number(idLector),
      activo,
      servicios: seleccionados.map((s) => ({
        id_tipo_servicio_alimentacion: Number(s.id_tipo_servicio_alimentacion),
        codigo: String(s.codigo).trim().toUpperCase(),
        activo: true,
      })),
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {estacion ? 'Editar estación offline' : 'Nueva estación offline'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Proyecto</Form.Label>
            <Form.Control
              type="text"
              value={selectedProject?.nombre || 'Sin proyecto'}
              readOnly
              className="bg-light"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Código *</Form.Label>
                <Form.Control
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="Ej: COM1-ALM-01"
                  maxLength={40}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Estación Comedor 1"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Comedor *</Form.Label>
                <Form.Select value={idComedor} onChange={(e) => handleComedorSelect(e.target.value)}>
                  <option value="">Seleccione...</option>
                  {comedores.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Lector *</Form.Label>
                <Form.Select
                  value={idLector}
                  onChange={(e) => setIdLector(e.target.value)}
                  disabled={!idComedor}
                >
                  <option value="">Seleccione...</option>
                  {lectoresFiltrados.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="estacion-activa"
              label="Activo"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
            />
          </Form.Group>

          {estacion ? (
            <Form.Group className="mb-3">
              <Form.Label>Token de estación (copiar en Electron)</Form.Label>
              <p className="text-muted small mb-2">
                Si el PC se daña, use el <strong>mismo token</strong> en el equipo nuevo. Solo
                regenere si el token se filtró o debe invalidarse.
              </p>
              <InputGroup>
                <Form.Control
                  type={showToken ? 'text' : 'password'}
                  value={tokenAcceso || ''}
                  readOnly
                  className="bg-light font-monospace"
                  placeholder="Se genera al crear la estación"
                />
                <Button
                  variant="outline-secondary"
                  title={showToken ? 'Ocultar' : 'Mostrar'}
                  onClick={() => setShowToken((v) => !v)}
                >
                  {showToken ? <FaEyeSlash /> : <FaEye />}
                </Button>
                <Button
                  variant="outline-primary"
                  title="Copiar"
                  disabled={!tokenAcceso}
                  onClick={handleCopyToken}
                >
                  <FaCopy /> {copyOk ? 'Copiado' : 'Copiar'}
                </Button>
                {onRegenerarToken && (
                  <Button
                    variant="outline-warning"
                    title="Regenerar token"
                    disabled={regenerando}
                    onClick={handleRegenerarToken}
                  >
                    <FaSync /> Regenerar
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          ) : (
            <div className="alert alert-info py-2 small">
              Al crear la estación se generará un token visible para copiar en Electron. Ese token
              se reutiliza si cambia el equipo.
            </div>
          )}

          <h6 className="mt-3 mb-2">Servicios habilitados *</h6>
          <p className="text-muted small mb-2">
            Marque los servicios que Electron podrá elegir al crear un turno (código corto DES/ALM/CEN).
          </p>
          <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <Table size="sm" bordered hover>
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Servicio</th>
                  <th style={{ width: 120 }}>Código</th>
                </tr>
              </thead>
              <tbody>
                {servicios.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted">
                      No hay tipos de servicio cargados
                    </td>
                  </tr>
                ) : (
                  servicios.map((s) => (
                    <tr key={s.id_tipo_servicio_alimentacion}>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={s.checked}
                          onChange={() => toggleServicio(s.id_tipo_servicio_alimentacion)}
                        />
                      </td>
                      <td>{s.descripcion}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={s.codigo}
                          disabled={!s.checked}
                          onChange={(e) =>
                            updateServicioCodigo(s.id_tipo_servicio_alimentacion, e.target.value)
                          }
                          maxLength={20}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {estacion ? 'Actualizar' : 'Crear'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalComedorLectorOffline;
